import { useEffect, useMemo, useState, useRef } from 'react'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Empty,
  Form,
  Grid,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Steps,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import {
  CheckCircleOutlined,
  DeleteOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  FileDoneOutlined,
} from '@ant-design/icons'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@tanstack/react-query'

import apiClient from '../../api/client'
import { countryOptions, isEUCountry as isEUCountryCode } from '../../utils/countries'
import PageHeader from '../../components/PageHeader'
import StripeCardSetup from '../../components/StripeCardSetup'
import { useCartStore } from '../../stores/cart'
import { trackAddPaymentInfo, trackBeginCheckout, trackGenerateLead, trackLogin, trackPurchase, trackRemoveFromCart, trackSignUp, trackViewCart } from '../../utils/shopTracking'

interface TaxPreview {
  tax_class_code: string
  rate_pct: string      // e.g. "19.000"
  reason: string        // "domestic" | "reverse_charge" | "oss" | "third_country" | "kleinunternehmer"
  note: string
  country: string
}

const TAX_REASON_COLORS: Record<string, string> = {
  domestic:         'blue',
  reverse_charge:   'green',
  oss:              'orange',
  oss_fallback:     'gold',
  third_country:    'cyan',
  kleinunternehmer: 'purple',
  customer_exempt:  'green',
}

// cycleLabels are resolved via t('billingCycle.*') at render time

function fmtPrice(amount: number, currency: string) {
  return `${amount.toFixed(2)} ${currency}`
}

// Friendly labels for the configured order options shown read-only in the cart.
// Only listed keys are displayed (keeps internal/technical keys hidden).
const CART_CFG_LABELS: Record<string, string> = {
  domain: 'Domain',
  transport_host: 'Zielserver',
  transport_port: 'Port',
  transport_tls: 'TLS',
  region: 'Region',
  max_mailboxes: 'Postfächer',
  max_nodes: 'Nodes',
  ssl_domain: 'Domain (CN)',
  approver_email: 'Approver-E-Mail',
  dcv_method: 'Validierung',
}

interface PlacedOrder { id: number; order_no?: string; status: string }
interface LoginResp { totp_required?: boolean; challenge_token?: string }

// ── OrderSummary (with live tax) ──────────────────────────────────────────────

interface OrderSummaryProps {
  subtotal: number
  discount: number
  currency: string
  coupon: string
  country: string
  vatId: string
  vatIdInput: string
  onCountryChange: (c: string) => void
  onVatIdApply: () => void
  onVatIdInputChange: (v: string) => void
  taxPreview?: TaxPreview
  taxLoading: boolean
  netSubtotal: number
  taxAmount: number
  grossTotal: number
  isEUCountry: boolean
}

function OrderSummary({
  subtotal, discount, currency, coupon,
  country, vatId, vatIdInput, onCountryChange, onVatIdApply, onVatIdInputChange,
  taxPreview, taxLoading, netSubtotal, taxAmount, grossTotal, isEUCountry,
}: OrderSummaryProps) {
  const { t, i18n } = useTranslation()
  const COUNTRY_OPTIONS = useMemo(() => countryOptions(i18n.language), [i18n.language])
  const setupTotal = useCartStore((s) =>
    s.items.reduce((acc, i) => acc + i.setup_fee * i.quantity, 0))

  const taxReasonLabels: Record<string, string> = {
    domestic:         t('portalCart.taxReasonDomestic', 'Inländische Lieferung'),
    reverse_charge:   t('portalCart.taxReasonReverseCharge', 'Reverse-Charge (B2B EU)'),
    oss:              t('portalCart.taxReasonOss', 'OSS (EU B2C)'),
    oss_fallback:     t('portalCart.taxReasonOssFallback', 'OSS (Fallback-Satz)'),
    third_country:    t('portalCart.taxReasonThirdCountry', 'Drittland — steuerfrei'),
    kleinunternehmer: t('portalCart.taxReasonKleinunternehmer', 'Kleinunternehmer §19'),
    customer_exempt:  t('portalCart.taxReasonCustomerExempt', 'Steuerbefreit'),
  }
  const reasonColor = taxPreview ? (TAX_REASON_COLORS[taxPreview.reason] || 'default') : null
  const reasonLabel = taxPreview ? (taxReasonLabels[taxPreview.reason] || taxPreview.reason) : null

  return (
    <Card size="small" className="content-card">
      <Typography.Title level={5} className="mt-0 mb-12">
        {t('portalCart.summaryTitle', 'Bestellübersicht')}
      </Typography.Title>

      {/* Country + VAT-ID */}
      <div className="mb-12">
        <Typography.Text type="secondary" className="text-sm block mb-4">
          <GlobalOutlined className="mr-4" />{t('portalCart.country', 'Land')}
        </Typography.Text>
        <Select
          value={country}
          onChange={onCountryChange}
          options={COUNTRY_OPTIONS}
          showSearch
          filterOption={(input, opt) =>
            String(opt?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          style={{ width: '100%' }}
          size="small"
        />
      </div>

      {isEUCountry && country !== 'DE' && (
        <div className="mb-12">
          <Typography.Text type="secondary" className="text-sm block mb-4">
            {t('portalCart.vatIdLabel', 'USt-IdNr. (optional, für Reverse-Charge)')}
            <Tooltip title={t('portalCart.vatIdTooltip', 'Gib deine USt-IdNr. an, wenn du ein Unternehmen bist. Bei valider EU-USt-IdNr. wird keine MwSt. berechnet (Reverse-Charge).')}>
              <InfoCircleOutlined className="ml-4" />
            </Tooltip>
          </Typography.Text>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="small"
              placeholder={`${country}123456789`}
              value={vatIdInput}
              onChange={(e) => onVatIdInputChange(e.target.value.toUpperCase())}
              onPressEnter={onVatIdApply}
            />
            <Button size="small" onClick={onVatIdApply}>{t('portalCart.vatIdCheck', 'Prüfen')}</Button>
          </Space.Compact>
          {vatId && (
            <Typography.Text type="success" className="text-xs mt-4 block">
              {t('portalCart.vatIdActive', 'Aktiv: {{vatId}}', { vatId })}
            </Typography.Text>
          )}
        </div>
      )}

      <Divider style={{ margin: '8px 0' }} />

      {/* Tax reason badge */}
      {taxPreview && !taxLoading && reasonColor && reasonLabel && (
        <div className="mb-8">
          <Tag color={reasonColor}>{reasonLabel}</Tag>
          {taxPreview.note && (
            <Typography.Text type="secondary" className="text-xs block mt-4">
              {taxPreview.note}
            </Typography.Text>
          )}
        </div>
      )}

      {/* Line items */}
      <div className="flex-between mb-6">
        <Typography.Text type="secondary">{t('portalCart.net', 'Netto')}</Typography.Text>
        <Typography.Text>{fmtPrice(subtotal, currency)}</Typography.Text>
      </div>
      {setupTotal > 0 && (
        <div className="flex-between mb-6">
          <Typography.Text type="secondary">{t('portalCart.setup', 'Einrichtung')}</Typography.Text>
          <Typography.Text>{fmtPrice(setupTotal, currency)}</Typography.Text>
        </div>
      )}
      {discount > 0 && (
        <div className="flex-between mb-6">
          <Typography.Text type="secondary">
            {t('portalCart.discount', 'Rabatt')} {coupon && <Tag color="green" style={{ marginLeft: 4 }}>{coupon}</Tag>}
          </Typography.Text>
          <Typography.Text type="success">−{fmtPrice(discount, currency)}</Typography.Text>
        </div>
      )}
      {netSubtotal !== subtotal - discount && (
        <div className="flex-between mb-6">
          <Typography.Text type="secondary">{t('portalCart.netTotal', 'Netto gesamt')}</Typography.Text>
          <Typography.Text>{fmtPrice(netSubtotal, currency)}</Typography.Text>
        </div>
      )}
      <div className="flex-between mb-6">
        <Typography.Text type="secondary">
          {taxLoading
            ? t('portalCart.vatCalculating', 'MwSt. (wird berechnet…)')
            : taxPreview
              ? `${t('portalCart.vat', 'MwSt.')} ${parseFloat(taxPreview.rate_pct).toFixed(0)}%`
              : t('portalCart.vat', 'MwSt.')}
        </Typography.Text>
        <Typography.Text>
          {taxLoading ? '…' : fmtPrice(taxAmount, currency)}
        </Typography.Text>
      </div>
      <Divider style={{ margin: '8px 0' }} />
      <div className="flex-between">
        <Typography.Text strong>{t('portalCart.grossTotal', 'Gesamtbetrag (brutto)')}</Typography.Text>
        <Typography.Text strong style={{ fontSize: 16, color: 'var(--branding-accent, #0ea5e9)' }}>
          {taxLoading ? '…' : fmtPrice(grossTotal + setupTotal, currency)}
        </Typography.Text>
      </div>
      <Typography.Text type="secondary" className="text-xs mt-4 block">
        {t('portalCart.priceHint', 'Alle Preise in {{currency}}, zzgl. gesetzl. MwSt. gemäß Lieferland.', { currency })}
      </Typography.Text>
    </Card>
  )
}

// ── AuthGate — inline Login + Register ───────────────────────────────────────

function AuthGate({ onSuccess }: { onSuccess: () => void }) {
  const { t, i18n } = useTranslation()
  const { message } = AntdApp.useApp()
  const [tab, setTab] = useState<'login' | 'register'>('login')
  const [challenge, setChallenge] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [regForm] = Form.useForm()
  const regCompany = Form.useWatch('company', regForm)
  const COUNTRY_OPTIONS = useMemo(() => countryOptions(i18n.language), [i18n.language])

  const onLogin = async (v: { email: string; password: string }) => {
    setSubmitting(true)
    try {
      const res = await apiClient.post<{ data: LoginResp }>('/api/v1/portal/auth/login', v, { _suppressRedirect: true })
      if (res.data.data.totp_required && res.data.data.challenge_token) {
        setChallenge(res.data.data.challenge_token)
        return
      }
      trackLogin('portal')
      onSuccess()
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      void message.error(err.response?.data?.message || t('portalCart.loginFailed', 'Login fehlgeschlagen'))
    } finally { setSubmitting(false) }
  }

  const onVerifyTotp = async () => {
    if (!challenge) return
    setSubmitting(true)
    try {
      await apiClient.post('/api/v1/portal/auth/verify-totp', { challenge_token: challenge, code: otp.trim() }, { _suppressRedirect: true })
      trackLogin('portal')
      onSuccess()
    } catch {
      void message.error(t('portalCart.totpInvalid', 'Code ungültig'))
    } finally { setSubmitting(false) }
  }

  const onRegister = async (v: {
    email: string; password: string; password_confirm: string
    first_name: string; last_name: string; company?: string
    phone?: string; street: string; postal_code: string; city: string; country: string
    tax_id?: string; tos_accept?: boolean
  }) => {
    if (v.password !== v.password_confirm) {
      void message.error(t('portalCart.passwordMismatch', 'Passwörter stimmen nicht überein'))
      return
    }
    setSubmitting(true)
    try {
      await apiClient.post('/api/v1/portal/signup', {
        email: v.email, password: v.password,
        first_name: v.first_name, last_name: v.last_name,
        company: v.company || undefined,
        phone: v.phone || undefined,
        tax_id: v.tax_id || undefined,
        street: v.street, postal_code: v.postal_code, city: v.city, country: v.country,
        tos_accept: !!v.tos_accept, locale: 'de',
      })
      void message.success(t('portalCart.registerSuccess', 'Account erstellt — du bist jetzt eingeloggt!'))
      trackSignUp('portal')
      trackGenerateLead()
      onSuccess()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } }
      void message.error(err.response?.data?.error || t('portalCart.registerFailed', 'Registrierung fehlgeschlagen'))
    } finally { setSubmitting(false) }
  }

  if (challenge) {
    return (
      <Card className="content-card">
        <Typography.Title level={5} className="mt-0">{t('portalCart.twoFaTitle', '2-Faktor-Authentifizierung')}</Typography.Title>
        <Form layout="vertical" onFinish={onVerifyTotp}>
          <Form.Item label={t('portalCart.twoFaCodeLabel', '6-stelliger Code')}>
            <Input value={otp} onChange={(e) => setOtp(e.target.value)}
              inputMode="numeric" maxLength={6} autoFocus autoComplete="one-time-code" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block loading={submitting}>
            {t('portalCart.twoFaConfirm', 'Bestätigen')}
          </Button>
        </Form>
      </Card>
    )
  }

  return (
    <Card className="content-card">
      <Typography.Title level={5} className="mt-0">
        <UserOutlined className="mr-8" />{t('portalCart.authTitle', 'Anmelden oder registrieren')}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {t('portalCart.authHint', 'Um deine Bestellung aufzugeben, melde dich an oder erstelle ein neues Konto.')}
      </Typography.Paragraph>
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as 'login' | 'register')}
        items={[
          {
            key: 'login', label: t('portalCart.tabLogin', 'Einloggen'),
            children: (
              <Form layout="vertical" onFinish={onLogin}>
                <Form.Item name="email" label={t('portalCart.fieldEmail', 'E-Mail')} rules={[{ required: true, type: 'email' }]}>
                  <Input autoComplete="email" />
                </Form.Item>
                <Form.Item name="password" label={t('portalCart.fieldPassword', 'Passwort')} rules={[{ required: true }]}>
                  <Input.Password autoComplete="current-password" />
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block loading={submitting}>
                    {t('portalCart.loginBtn', 'Einloggen')}
                  </Button>
                </Form.Item>
                <Typography.Text type="secondary" className="text-sm">
                  <Link to="/portal/reset-request">{t('portalCart.forgotPassword', 'Passwort vergessen?')}</Link>
                </Typography.Text>
              </Form>
            ),
          },
          {
            key: 'register', label: t('portalCart.tabRegister', 'Neues Konto'),
            children: (
              <Form form={regForm} layout="vertical" onFinish={onRegister}>
                <Row gutter={12}>
                  <Col xs={24} sm={12}>
                    <Form.Item name="first_name" label={t('portalCart.fieldFirstName', 'Vorname')} rules={[{ required: true }]}>
                      <Input autoComplete="given-name" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item name="last_name" label={t('portalCart.fieldLastName', 'Nachname')} rules={[{ required: true }]}>
                      <Input autoComplete="family-name" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="company" label={t('portalCart.fieldCompany', 'Firma (optional)')}>
                  <Input autoComplete="organization" />
                </Form.Item>
                <Form.Item name="street" label={t('portalCart.fieldStreet', 'Straße & Hausnummer')} rules={[{ required: true }]}>
                  <Input autoComplete="street-address" />
                </Form.Item>
                <Row gutter={12}>
                  <Col xs={24} sm={8}>
                    <Form.Item name="postal_code" label={t('portalCart.fieldPostalCode', 'PLZ')} rules={[{ required: true }]}>
                      <Input autoComplete="postal-code" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={16}>
                    <Form.Item name="city" label={t('portalCart.fieldCity', 'Ort')} rules={[{ required: true }]}>
                      <Input autoComplete="address-level2" />
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="country" label={t('portalCart.fieldCountry', 'Land')} initialValue="DE" rules={[{ required: true }]}>
                  <Select showSearch optionFilterProp="label" options={COUNTRY_OPTIONS} />
                </Form.Item>
                {regCompany && String(regCompany).trim() !== '' && (
                  <Form.Item name="tax_id" label={t('portalCart.fieldVatId', 'USt-IdNr. (bei Firma im Ausland)')}
                    extra={t('portalCart.fieldVatIdHint', 'Bei gültiger EU-USt-IdNr. wird keine MwSt. berechnet (Reverse-Charge).')}>
                    <Input placeholder="ATU12345678" autoComplete="off" />
                  </Form.Item>
                )}
                <Form.Item name="phone" label={t('portalCart.fieldPhone', 'Telefon')}
                  rules={[{ required: true, message: t('portalCart.phoneRequired', 'Telefonnummer ist erforderlich') }]}>
                  <Input autoComplete="tel" placeholder="+49 …" />
                </Form.Item>
                <Form.Item name="email" label={t('portalCart.fieldEmail', 'E-Mail')} rules={[{ required: true, type: 'email' }]}>
                  <Input autoComplete="email" />
                </Form.Item>
                <Form.Item name="password" label={t('portalCart.fieldPassword', 'Passwort')} rules={[{ required: true, min: 10 }]}>
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Form.Item name="password_confirm" label={t('portalCart.fieldPasswordConfirm', 'Passwort bestätigen')} rules={[{ required: true }]}>
                  <Input.Password autoComplete="new-password" />
                </Form.Item>
                <Form.Item name="tos_accept" valuePropName="checked"
                  rules={[{ validator: (_, val) => val ? Promise.resolve() : Promise.reject(new Error(t('portalCart.tosRequired', 'Bitte AGB akzeptieren'))) }]}>
                  <Checkbox>{t('portalCart.tosAccept', 'Ich akzeptiere die AGB und Datenschutzerklärung.')}</Checkbox>
                </Form.Item>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block loading={submitting}>
                    {t('portalCart.registerBtn', 'Konto erstellen & weiter')}
                  </Button>
                </Form.Item>
              </Form>
            ),
          },
        ]}
      />
    </Card>
  )
}

// ── CartPage ─────────────────────────────────────────────────────────────────

export default function CartPage() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const screens = Grid.useBreakpoint()
  const { items, coupon, removeItem, updateQuantity, updateItemConfig, setCoupon, clear } = useCartStore()

  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null)
  const [couponInput, setCouponInput] = useState(coupon)
  const [couponDiscount, setCouponDiscount] = useState(0)
  const [placedOrders, setPlacedOrders] = useState<PlacedOrder[]>([])
  const [currentStep, setCurrentStep] = useState(0)
  const [country, setCountry] = useState('DE')
  const [vatId, setVatId] = useState('')
  const [vatIdInput, setVatIdInput] = useState('')
  // Pflicht-Einwilligungen vor der Bestellung (Verbraucherrecht).
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [acceptWithdrawal, setAcceptWithdrawal] = useState(false)
  const legalOk = acceptTerms && acceptWithdrawal
  // Webhosting-Produkte brauchen zwingend eine Domain.
  const domainsOk = items.every(i => i.kind !== 'hosting' || !!(i.config?.domain as string)?.trim?.())
  // Vom Kunden gewählte Zahlart (Standard für künftige Rechnungen).
  const [paymentMethod, setPaymentMethod] = useState('sepa')
  const [cardModalOpen, setCardModalOpen] = useState(false)
  const cardQ = useQuery({
    queryKey: ['portal-pm-card'],
    queryFn: async () => (await apiClient.get<{ data: { stripe_card: boolean } }>('/api/v1/portal/payment-methods/manage', { _suppressRedirect: true })).data.data,
    enabled: !!isLoggedIn,
  })
  const cardOnFile = !!cardQ.data?.stripe_card
  // Finalize a vaulted card after the SCA/3DS redirect returns to the cart.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const si = params.get('setup_intent')
    if (!si) return
    if (params.get('redirect_status') === 'succeeded') {
      apiClient.post('/api/v1/portal/payment-methods/stripe/finalize', { setup_intent_id: si })
        .then(() => { void message.success(t('portalCart.cardSaved', 'Kreditkarte hinterlegt ✓')); void cardQ.refetch() })
        .catch(() => void message.error(t('portalCart.cardFailed', 'Karte konnte nicht gespeichert werden')))
    }
    params.delete('setup_intent'); params.delete('setup_intent_client_secret'); params.delete('redirect_status')
    const qs = params.toString()
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? '?' + qs : ''}`)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const payMethodsQ = useQuery({
    // Only needed once logged in (payment options are shown after login). Gating
    // on isLoggedIn + suppressing the 401-redirect keeps guests on /cart (with the
    // login/register tabs) instead of bouncing them to /portal/login.
    enabled: !!isLoggedIn,
    queryKey: ['portal-payment-methods'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { stripe: boolean; mollie: boolean; paypal: boolean; banktransfer: boolean } }>(
        '/api/v1/portal/payment-methods', { _suppressRedirect: true })
      return res.data.data
    },
    staleTime: 60_000,
  })

  // Check auth status on mount
  useEffect(() => {
    apiClient.get('/api/v1/portal/me', { _suppressRedirect: true })
      .then(() => setIsLoggedIn(true))
      .catch(() => setIsLoggedIn(false))
  }, [])

  const currency = items[0]?.currency || 'EUR'
  const subtotal = items.reduce((acc, i) => acc + i.total_price * i.quantity, 0)

  // Tax preview — fetch whenever country or vatId changes.
  // We use the first product's ID as representative; most shops have a
  // uniform tax class. If products have different classes a v2 can fan-out.
  const firstProductId = items[0]?.product_id
  const taxQ = useQuery<TaxPreview>({
    queryKey: ['tax-preview', country, vatId, firstProductId],
    queryFn: async () => {
      const params = new URLSearchParams({ customer_country: country })
      if (vatId.trim()) params.set('customer_vat_id', vatId.trim())
      if (firstProductId) params.set('product_id', String(firstProductId))
      const res = await apiClient.get<{ data: TaxPreview }>(
        `/api/v1/public/tax-preview?${params}`,
        { _suppressRedirect: true },
      )
      return res.data.data
    },
    enabled: items.length > 0,
    staleTime: 60_000,
  })

  const taxRate = parseFloat(taxQ.data?.rate_pct || '0') / 100
  const netSubtotal = subtotal - couponDiscount
  const taxAmount = Math.round(netSubtotal * taxRate * 100) / 100
  const grossTotal = netSubtotal + taxAmount
  const isEUCountry = isEUCountryCode(country)

  // GA4 view_cart — once, when the cart has items.
  useEffect(() => {
    if (items.length > 0) trackViewCart(items, grossTotal, { netEur: netSubtotal, taxEur: taxAmount })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length])

  const applyCoupon = useMutation({
    mutationFn: async () => {
      if (!couponInput.trim() || items.length === 0) return
      const res = await apiClient.post<{ data: { discount_amount: string } }>(
        '/api/v1/portal/coupons/validate',
        {
          code: couponInput.trim(),
          currency,
          subtotal: subtotal.toFixed(2),
          product_ids: items.map((i) => i.product_id),
        },
      )
      return res.data.data
    },
    onSuccess: (d) => {
      if (d) {
        setCouponDiscount(parseFloat(d.discount_amount))
        setCoupon(couponInput.trim())
        void message.success(t('portalCart.couponApplied', 'Gutschein angewendet'))
      }
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      setCouponDiscount(0)
      setCoupon('')
      void message.error(err.response?.data?.error || t('portalCart.couponInvalid', 'Gutschein ungültig'))
    },
  })

  const placeOrders = useMutation({
    mutationFn: async () => {
      const results: PlacedOrder[] = []
      for (const item of items) {
        const res = await apiClient.post<{ data: PlacedOrder }>('/api/v1/portal/orders', {
          product_id: item.product_id,
          quantity: item.quantity,
          billing_cycle: item.billing_cycle,
          coupon_code: coupon || undefined,
          config: Object.keys(item.config).length > 0 ? item.config : undefined,
          payment_method: paymentMethod,
          accept_terms: acceptTerms,
          accept_withdrawal: acceptWithdrawal,
        })
        results.push(res.data.data)
      }
      return results
    },
    onSuccess: (orders) => {
      // GA4 purchase — build from the cart BEFORE it's cleared.
      const orderNo = orders[0]?.order_no || (orders[0] ? `order-${orders[0].id}` : 'order')
      trackPurchase(orderNo, items, {
        grossEur: grossTotal, netEur: netSubtotal, taxEur: taxAmount,
        coupon: coupon || undefined, payment: paymentMethod,
      })
      setPlacedOrders(orders)
      setCurrentStep(2)
      clear()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      void message.error(err.response?.data?.error || t('portalCart.orderFailed', 'Bestellung fehlgeschlagen'))
    },
  })

  const handleAuthSuccess = () => {
    setIsLoggedIn(true)
    setCurrentStep(1)
  }

  // GA4 checkout events must fire exactly once per checkout on BOTH ways to the
  // order button (step 0 via handleCheckout, and step 1 after login/registration).
  // begin_checkout once per checkout; add_payment_info once per payment method.
  // The track* wrappers swallow their own errors, so none of this can disturb the order.
  const beginCheckoutSent = useRef(false)
  const paymentInfoSentFor = useRef<string | null>(null)
  const trackCheckoutOnce = () => {
    if (!beginCheckoutSent.current) {
      beginCheckoutSent.current = true
      trackBeginCheckout(items, grossTotal, { taxEur: taxAmount, coupon: coupon || undefined })
    }
  }
  const trackPaymentInfoOnce = () => {
    if (paymentInfoSentFor.current !== paymentMethod) {
      paymentInfoSentFor.current = paymentMethod
      trackAddPaymentInfo(paymentMethod, items, grossTotal)
    }
  }

  const handleCheckout = () => {
    if (!domainsOk) {
      message.error(t('portalCart.hostingDomainRequired', 'Bitte eine Domain angeben — Pflicht bei Webhosting.'))
      return
    }
    trackCheckoutOnce()
    if (!isLoggedIn) {
      setCurrentStep(1)
    } else {
      trackPaymentInfoOnce()
      placeOrders.mutate()
    }
  }

  // Order button of step 1 (already logged in, or just logged in / registered).
  const handlePlaceOrderStep1 = () => {
    trackCheckoutOnce() // covers arriving at step 1 without having passed handleCheckout
    trackPaymentInfoOnce()
    placeOrders.mutate()
  }

  // ── Success page ───────────────────────────────────────────────────────────
  if (placedOrders.length > 0) {
    return (
      <>
        <PageHeader
          icon={<CheckCircleOutlined />}
          title={t('portalCart.successTitle', 'Bestellung aufgegeben')}
          subtitle={t('portalCart.successSubtitle', 'Vielen Dank für deine Bestellung!')}
        />
        <Card className="content-card" style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 48, color: '#10b981' }} />
            <Typography.Title level={3} className="mt-16 mb-8">
              {t('portalCart.successHeadline', 'Bestellung erfolgreich!')}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              {t('portalCart.successBody', 'Deine Bestellung wurde aufgenommen und wird jetzt bearbeitet.')}
            </Typography.Paragraph>
          </div>
          <Divider />
          {placedOrders.map((o) => (
            <div key={o.id} className="flex-between mb-8">
              <Typography.Text>
                {t('portalCart.orderRef', 'Bestellung #{{ref}}', { ref: o.order_no || o.id })}
              </Typography.Text>
              <Tag color={o.status === 'complete' ? 'green' : 'blue'}>
                {o.status === 'complete'
                  ? t('portalCart.statusComplete', 'Abgeschlossen')
                  : t('portalCart.statusProcessing', 'In Bearbeitung')}
              </Tag>
            </div>
          ))}
          <Divider />
          <Space wrap>
            <Button type="primary" onClick={() => navigate('/portal/orders')}>
              {t('portalCart.myOrders', 'Meine Bestellungen')}
            </Button>
            <Button onClick={() => navigate('/portal/billing?tab=invoices')}>
              {t('portalCart.invoices', 'Rechnungen')}
            </Button>
            <Button onClick={() => navigate('/portal/shop')}>
              {t('portalCart.continueShopping', 'Weiter einkaufen')}
            </Button>
          </Space>
        </Card>
      </>
    )
  }

  // ── Empty cart ─────────────────────────────────────────────────────────────
  if (items.length === 0 && currentStep === 0) {
    return (
      <>
        <PageHeader
          icon={<ShoppingCartOutlined />}
          title={t('cart.title', 'Warenkorb')}
        />
        <Card className="content-card">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('portalCart.emptyCart', 'Dein Warenkorb ist leer')}
          >
            <Button type="primary" onClick={() => navigate('/portal/shop')}>
              {t('portalCart.toShop', 'Zum Shop')}
            </Button>
          </Empty>
        </Card>
      </>
    )
  }

  const steps = [
    { title: t('portalCart.stepCart', 'Warenkorb'), icon: <ShoppingCartOutlined /> },
    { title: t('portalCart.stepLogin', 'Anmelden'), icon: <UserOutlined /> },
    { title: t('portalCart.stepConfirm', 'Bestätigung'), icon: <FileDoneOutlined /> },
  ]

  const visibleStep = isLoggedIn ? Math.min(currentStep, 0) : currentStep

  return (
    <>
      <PageHeader
        icon={<ShoppingCartOutlined />}
        title={t('cart.title', 'Warenkorb')}
        subtitle={t('portalCart.itemCount', '{{count}} Artikel', { count: items.length })}
      />

      {!isLoggedIn && (
        <Steps
          current={visibleStep}
          items={steps}
          size="small"
          className="mb-24"
          style={{ maxWidth: 480 }}
        />
      )}

      <Row gutter={[16, 16]}>
        {/* Left: items + coupon + auth */}
        <Col xs={24} lg={16}>
          {/* Cart items */}
          <Card className="content-card mb-16">
            <Typography.Title level={5} className="mt-0">{t('portalCart.items', 'Artikel')}</Typography.Title>
            {items.map((item) => (
              <div key={item.slug}>
                <div className={screens.sm ? 'flex-between' : undefined} style={{ gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <Typography.Text strong>{item.name}</Typography.Text>
                    <div>
                      <Typography.Text type="secondary" className="text-sm">
                        {t(`billingCycle.${item.billing_cycle}`, item.billing_cycle)}
                        {item.setup_fee > 0 && ` · ${t('portalCart.setup', 'Einrichtung')} ${fmtPrice(item.setup_fee, item.currency)}`}
                      </Typography.Text>
                    </div>
                  </div>
                  <Space align="center" style={{ marginTop: screens.sm ? 0 : 8 }}>
                    <InputNumber
                      min={1} max={99} size="small"
                      value={item.quantity}
                      onChange={(v) => updateQuantity(item.slug, v ?? 1)}
                      style={{ width: 60 }}
                    />
                    <Typography.Text strong>
                      {fmtPrice(item.total_price * item.quantity, item.currency)}
                    </Typography.Text>
                    <Button
                      type="text" size="small" danger
                      icon={<DeleteOutlined />}
                      onClick={() => { trackRemoveFromCart(item); removeItem(item.slug) }}
                    />
                  </Space>
                </div>
                {item.kind === 'domain' && item.config?.domain != null && (
                  <div style={{ marginTop: 8 }}>
                    <Typography.Text type="secondary" className="text-sm">
                      {t('portalCart.customNs', 'Eigene Nameserver (optional, je Zeile einer — leer = unsere DNS)')}
                    </Typography.Text>
                    <Input.TextArea
                      rows={2}
                      placeholder={'ns1.example.com\nns2.example.com'}
                      defaultValue={Array.isArray(item.config?.nameservers)
                        ? (item.config.nameservers as string[]).join('\n') : ''}
                      onBlur={(e) => {
                        const list = e.target.value.split('\n').map((s) => s.trim()).filter(Boolean)
                        const cfg = { ...item.config }
                        if (list.length > 0) cfg.nameservers = list
                        else delete cfg.nameservers
                        updateItemConfig(item.slug, cfg)
                      }}
                      style={{ marginTop: 4 }}
                    />
                  </div>
                )}
                {item.kind === 'hosting' && (
                  <div style={{ marginTop: 8 }}>
                    {(item.config?.domain as string)?.trim?.() ? (
                      <Typography.Text className="text-sm">
                        {t('portalCart.hostingDomainLabel', 'Domain für dieses Hosting')}:{' '}
                        <strong>{item.config?.domain as string}</strong>
                        {item.config?.inclusive_domains
                          ? ` · ${t('portalCart.fromProductPage', 'auf der Produktseite gewählt')}`
                          : ''}
                      </Typography.Text>
                    ) : (
                      <>
                        <Typography.Text className="text-sm">
                          {t('portalCart.hostingDomainLabel', 'Domain für dieses Hosting')} *
                        </Typography.Text>
                        <Input
                          placeholder="meinedomain.de"
                          status="error"
                          onBlur={(e) => {
                            const cfg = { ...item.config }
                            const d = e.target.value.trim().toLowerCase()
                            if (d) cfg.domain = d
                            else delete cfg.domain
                            updateItemConfig(item.slug, cfg)
                          }}
                          style={{ marginTop: 4 }}
                        />
                        <Typography.Text type="danger" className="text-sm">
                          {t('portalCart.hostingDomainRequired', 'Bitte eine Domain angeben — Pflicht bei Webhosting.')}
                        </Typography.Text>
                      </>
                    )}
                  </div>
                )}
                {/* Read-only summary of the options the customer configured. */}
                {(() => {
                  const entries = Object.entries(item.config || {})
                    .filter(([k, v]) => CART_CFG_LABELS[k] && v != null && v !== '' && typeof v !== 'object')
                  if (entries.length === 0) return null
                  return (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '4px 18px' }}>
                      {entries.map(([k, v]) => (
                        <Typography.Text key={k} type="secondary" className="text-sm">
                          {CART_CFG_LABELS[k]}: <Typography.Text strong>{String(v)}</Typography.Text>
                        </Typography.Text>
                      ))}
                    </div>
                  )
                })()}
                <Divider style={{ margin: '12px 0' }} />
              </div>
            ))}

            {/* Coupon */}
            <div>
              <Typography.Text type="secondary" className="text-sm">
                {t('portalCart.couponCode', 'Gutscheincode')}
              </Typography.Text>
              <Space.Compact className="mt-6 w-full" style={{ maxWidth: 320 }}>
                <Input
                  placeholder="CODE"
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                  onPressEnter={() => applyCoupon.mutate()}
                />
                <Button
                  onClick={() => applyCoupon.mutate()}
                  loading={applyCoupon.isPending}
                  disabled={!couponInput.trim()}
                >
                  {t('portalCart.couponRedeem', 'Einlösen')}
                </Button>
              </Space.Compact>
              {couponDiscount > 0 && (
                <Typography.Text type="success" className="mt-6 block text-sm">
                  {t('portalCart.discountApplied', 'Rabatt: −{{amount}}', { amount: fmtPrice(couponDiscount, currency) })}
                </Typography.Text>
              )}
            </div>
          </Card>

          {/* Country hint */}
          {currentStep === 0 && (
            <Alert
              type="info"
              showIcon
              className="mb-16"
              message={t('portalCart.countryHint', 'Bitte wähle dein Land rechts aus, damit die korrekte Steuer berechnet wird.')}
              style={{ borderRadius: 8 }}
            />
          )}

          {/* Auth gate (step 1) */}
          {currentStep === 1 && !isLoggedIn && (
            <AuthGate onSuccess={handleAuthSuccess} />
          )}
        </Col>

        {/* Right: summary + CTA */}
        <Col xs={24} lg={8}>
          <OrderSummary
            subtotal={subtotal}
            discount={couponDiscount}
            currency={currency}
            coupon={coupon}
            country={country}
            vatId={vatId}
            vatIdInput={vatIdInput}
            onCountryChange={(c) => { setCountry(c); setVatId(''); setVatIdInput('') }}
            onVatIdApply={() => setVatId(vatIdInput.trim())}
            onVatIdInputChange={setVatIdInput}
            taxPreview={taxQ.data}
            taxLoading={taxQ.isLoading || taxQ.isFetching}
            netSubtotal={netSubtotal}
            taxAmount={taxAmount}
            grossTotal={grossTotal}
            isEUCountry={isEUCountry}
          />
          {isLoggedIn && (
            <div className="mt-12">
              <Typography.Text strong style={{ display: 'block', marginBottom: 6 }}>
                {t('portalCart.paymentMethod', 'Zahlungsart')}
              </Typography.Text>
              <Radio.Group
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
              >
                <Radio value="sepa">{t('portalCart.paySepa', 'SEPA-Lastschrift (Mandat)')}</Radio>
                {payMethodsQ.data?.banktransfer && (
                  <Radio value="banktransfer">{t('portalCart.payBank', 'Überweisung / Echtzeitüberweisung')}</Radio>
                )}
                {payMethodsQ.data?.stripe && (
                  <Radio value="stripe">{t('portalCart.payStripe', 'Kreditkarte (Stripe)')}</Radio>
                )}
                {payMethodsQ.data?.paypal && (
                  <Radio value="paypal">{t('portalCart.payPaypal', 'PayPal')}</Radio>
                )}
                {payMethodsQ.data?.mollie && (
                  <Radio value="mollie">{t('portalCart.payMollie', 'Mollie')}</Radio>
                )}
              </Radio.Group>
              {paymentMethod === 'banktransfer' && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message={t('portalCart.payBankRefRequired', 'Bitte gib bei der Überweisung unbedingt die Rechnungsnummer als Verwendungszweck an — sonst kann deine Zahlung nicht automatisch zugeordnet werden. Du erhältst eine Rechnung mit unseren Bankdaten.')}
                />
              )}
              {paymentMethod === 'stripe' && isLoggedIn && (
                <div style={{ marginTop: 8 }}>
                  {cardOnFile ? (
                    <Alert type="success" showIcon
                      message={t('portalCart.payStripeStored', 'Kreditkarte hinterlegt — fällige Rechnungen werden automatisch abgebucht.')} />
                  ) : (
                    <>
                      <Alert type="info" showIcon style={{ marginBottom: 8 }}
                        message={t('portalCart.payStripeHint', 'Hinterlege deine Kreditkarte, damit fällige Rechnungen automatisch abgebucht werden.')} />
                      <Button size="small" onClick={() => setCardModalOpen(true)}>
                        {t('portalCart.addCard', 'Kreditkarte hinterlegen')}
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
          {isLoggedIn && (
            <div className="mt-12" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)}>
                {t('portalCart.acceptTermsPre', 'Ich akzeptiere die')}{' '}
                <a href="/legal/agb" target="_blank" rel="noreferrer">{t('portalCart.terms', 'AGB')}</a>{' '}
                {t('portalCart.and', 'und')}{' '}
                <a href="/legal/datenschutz" target="_blank" rel="noreferrer">{t('portalCart.privacy', 'Datenschutzerklärung')}</a>.
              </Checkbox>
              <Checkbox checked={acceptWithdrawal} onChange={(e) => setAcceptWithdrawal(e.target.checked)}>
                {t('portalCart.acceptWithdrawalPre', 'Ich habe die')}{' '}
                <a href="/legal/widerruf" target="_blank" rel="noreferrer">{t('portalCart.withdrawal', 'Widerrufsbelehrung')}</a>{' '}
                {t('portalCart.acceptWithdrawalPost', 'zur Kenntnis genommen.')}
              </Checkbox>
            </div>
          )}
          <div className="mt-12">
            {currentStep === 0 && (
              <Button
                type="primary" block size="large"
                onClick={handleCheckout}
                loading={placeOrders.isPending}
                disabled={(!!isLoggedIn && !legalOk) || !domainsOk}
              >
                {isLoggedIn ? t('portalCart.placeOrder', 'Jetzt kostenpflichtig bestellen') : t('portalCart.toCheckout', 'Weiter zur Kasse')}
              </Button>
            )}
            {currentStep === 1 && isLoggedIn && (
              <Button
                type="primary" block size="large"
                onClick={handlePlaceOrderStep1}
                loading={placeOrders.isPending}
                disabled={!legalOk || !domainsOk}
              >
                {t('portalCart.placeOrder', 'Jetzt kostenpflichtig bestellen')}
              </Button>
            )}
            <Button
              block className="mt-8"
              onClick={() => navigate(-1)}
            >
              {t('portalCart.continueShopping', 'Weiter einkaufen')}
            </Button>
          </div>
          <Typography.Paragraph type="secondary" className="text-xs mt-12">
            {t('portalCart.legalNote', 'Mit deiner Bestellung akzeptierst du unsere AGB und Datenschutzerklärung.')}
          </Typography.Paragraph>
        </Col>
      </Row>
      <StripeCardSetup open={cardModalOpen} onClose={() => setCardModalOpen(false)} onDone={() => cardQ.refetch()} />
    </>
  )
}
